import { Component, OnInit, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AvoirService, Avoir, AvoirLine } from '../service/avoir.service';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ReturnOrderComponent } from '../return-order/return-order.component';
import { AuthService } from '../service/auth.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-avoir-detail',
  templateUrl: './avoir-detail.component.html',
  styleUrls: ['./avoir-detail.component.css'],
  providers: [DatePipe]
})
export class AvoirDetailComponent implements OnInit {
  avoir: Avoir | null = null;
  isLoading = false;
  recoveredCapital = 0;
  lostCapital = 0;
  showActionsMenu = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private avoirService: AvoirService,
    private datePipe: DatePipe,
    private dialog: MatDialog,
    private authService: AuthService
  ) { }

  // Custom Confirmation Dialog State
  isConfirmVisible = false;
  confirmTitle = '';
  confirmMessage = '';
  confirmType: 'info' | 'warning' | 'danger' = 'info';
  confirmAction: (() => void) | null = null;
  confirmBtnText = 'Confirmer';

  openConfirm(title: string, message: string, type: 'info' | 'warning' | 'danger', action: () => void, btnText: string = 'Confirmer'): void {
    this.confirmTitle = title;
    this.confirmMessage = message;
    this.confirmType = type;
    this.confirmAction = action;
    this.confirmBtnText = btnText;
    this.isConfirmVisible = true;
  }

  handleConfirm(): void {
    if (this.confirmAction) {
      this.confirmAction();
    }
    this.isConfirmVisible = false;
  }

  handleCancel(): void {
    this.isConfirmVisible = false;
    this.confirmAction = null;
  }

  ngOnInit(): void {
    // We subscribe to params to ensure the view reloads if the ID changes
    // while the component is already active (e.g., editing from the detail page)
    this.route.params.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.loadAvoir(Number(id));
      }
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.actions-menu-container')) {
      this.showActionsMenu = false;
    }
  }

  toggleActionsMenu(event: Event): void {
    event.stopPropagation();
    this.showActionsMenu = !this.showActionsMenu;
  }

  loadAvoir(id: number): void {
    this.isLoading = true;
    this.avoirService.getAvoirById(id).subscribe({
      next: (data) => {
        this.avoir = data;
        this.calculateCapitalAudit();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading avoir details:', err);
        this.isLoading = false;
        alert('Avoir introuvable.');
        this.router.navigate(['/avoir-list']);
      }
    });
  }

  calculateCapitalAudit(): void {
    if (!this.avoir || !this.avoir.avoirLines) return;
    
    this.recoveredCapital = 0;
    this.lostCapital = 0;

    this.avoir.avoirLines.forEach((line: AvoirLine) => {
      const purchasePrice = line.refundPurchasePrice || 0;
      const amount = purchasePrice * line.quantityReturned;
      if (line.isDefective) {
        this.lostCapital += amount;
      } else {
        this.recoveredCapital += amount;
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/avoir-list']);
  }

  formatDate(date: any): string {
    return this.datePipe.transform(date, 'dd/MM/yyyy HH:mm') || '';
  }

  isAdmin(): boolean {
    const user = this.authService.getUser();
    return user && user.role === 'ADMIN';
  }

  isAuthor(): boolean {
    if (!this.avoir) return false;
    const user = this.authService.getUser();
    return user && (user.username === this.avoir.createdBy || !this.avoir.createdBy);
  }

  modifyAvoir(): void {
    if (!this.avoir) return;
    const isApproved = this.avoir.status === 'APPROVED' || this.avoir.status === 'COMPLETED';
    
    if (isApproved) {
      this.openConfirm(
        'Action Interdite',
        'Un avoir déjà validé et clos ne peut plus être modifié pour garantir la cohérence financière.',
        'warning',
        () => {},
        'Compris'
      );
      return;
    }

    const isAdmin = this.isAdmin();
    const canModify = isAdmin || this.isAuthor();
    
    if (!canModify) {
      this.openConfirm(
        'Accès Refusé',
        'Seul l\'auteur de cette demande ou un administrateur peut la modifier.',
        'warning',
        () => {},
        'Compris'
      );
      return;
    }

    this.openEditDialog();
  }

  private openEditDialog(): void {
    const dialogRef = this.dialog.open(ReturnOrderComponent, {
      width: '1000px',
      maxWidth: '95vw',
      data: { 
        order: this.avoir!.originalOrder, 
        avoir: this.avoir 
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadAvoir(this.avoir!.id!);
      }
    });
  }

  approveAvoir(): void {
    if (!this.avoir) return;
    this.openConfirm(
      'Approuver Définitivement',
      'Voulez-vous approuver cet avoir ? Cela validera les mouvements de stock et de caisse historiques liés à aujourd\'hui. Cette action est irréversible.',
      'info',
      () => {
        this.avoirService.approveAvoir(this.avoir!.id!).subscribe({
          next: (data) => {
            this.avoir = data;
            this.calculateCapitalAudit();
          },
          error: (err) => {
            console.error(err);
            this.openConfirm(
              'Validation Échouée',
              err.error?.message || 'Une erreur est survenue lors de la validation.',
              'danger',
              () => {},
              'Compris'
            );
          }
        });
      },
      'Confirmer la Validation'
    );
  }

  printAvoir(): void {
    if (!this.avoir) return;
    this.generatePDF(this.avoir);
  }

  private generatePDF(avoir: Avoir): void {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(20);
    doc.text('BON DE RETOUR', 105, 20, { align: 'center' });

    doc.setFontSize(12);
    doc.text(`Avoir ID : #${avoir.id}`, 14, 40);
    doc.text(`Commande Originale : #${avoir.originalOrderId}`, 14, 48);
    doc.text(`Date du Retour : ${this.formatDate(avoir.avoirDate)}`, 14, 56);

    if (avoir.originalOrder?.clientDTO) {
      const client = avoir.originalOrder.clientDTO;
      doc.text(`Client : ${client.firstName || ''} ${client.lastName || ''}`, 14, 64);
    }

    if (avoir.notes) {
      doc.text(`Notes : ${avoir.notes}`, 14, 72);
    }

    // Table Content
    const tableData = avoir.avoirLines!.map(line => [
      line.product?.name,
      line.refundSellingPrice?.toFixed(2),
      line.quantityReturned,
      line.isDefective ? 'Oui' : 'Non',
      ((line.refundSellingPrice || 0) * line.quantityReturned).toFixed(2)
    ]);

    autoTable(doc, {
      startY: 85,
      head: [['Produit', 'Prix U. (MAD)', 'Qté', 'Défectueux', 'Total (MAD)']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185] }
    });

    // Calculations
    const totalArticles = this.calculationTotalArticles(avoir);
    const discountAdj = avoir.discountAdjustment || 0;
    const absorbedDebt = avoir.amountAbsorbedFromDebt || 0;
    const finalCash = avoir.amountRefundedInCash || 0;

    // Footer Total
    const finalY = (doc as any).lastAutoTable.finalY || 110;
    doc.setFontSize(11);
    doc.text(`Total Articles : ${totalArticles.toFixed(2)} MAD`, 140, finalY + 10, { align: 'right' });
    
    let currentY = finalY + 17;
    if (discountAdj > 0) {
      doc.text(`Ajustement Réduction : -${discountAdj.toFixed(2)} MAD`, 140, currentY, { align: 'right' });
      currentY += 7;
    }

    if (absorbedDebt > 0) {
      doc.setTextColor(31, 128, 185); // Blue for debt
      doc.text(`Dette Absorbée : -${absorbedDebt.toFixed(2)} MAD`, 140, currentY, { align: 'right' });
      currentY += 10;
      doc.setTextColor(0, 0, 0); // Back to black
    }

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38); // Red for final cash
    doc.text(`REMBOURSEMENT EFFECTIF (CASH) : ${finalCash.toFixed(2)} MAD`, 140, currentY, { align: 'right' });

    doc.save(`Avoir_${avoir.id}.pdf`);
  }

  private calculationTotalArticles(avoir: Avoir): number {
    if (!avoir.avoirLines) return 0;
    return avoir.avoirLines.reduce((sum, line) => sum + ((line.refundSellingPrice || 0) * line.quantityReturned), 0);
  }

  deleteAvoir(): void {
    if (!this.avoir) return;
    const isApproved = this.avoir.status === 'APPROVED' || this.avoir.status === 'COMPLETED';

    if (isApproved) {
      this.openConfirm(
        'Action Interdite',
        'Un avoir déjà validé ne peut plus être supprimé pour garantir l\'intégrité des données historiques.',
        'warning',
        () => {},
        'Compris'
      );
      return;
    }

    const isAdmin = this.isAdmin();
    if (!isAdmin && !this.isAuthor()) {
      this.openConfirm(
        'Accès Refusé',
        'Seul l\'auteur ou un administrateur peut supprimer cette demande.',
        'warning',
        () => {},
        'Compris'
      );
      return;
    }

    this.openConfirm(
      'Supprimer l\'Avoir',
      'Êtes-vous sûr de vouloir supprimer cette demande d\'avoir ?',
      'danger',
      () => {
        this.avoirService.deleteAvoir(this.avoir!.id!).subscribe({
          next: () => this.router.navigate(['/avoir-list']),
          error: (err) => console.error(err)
        });
      },
      'Supprimer'
    );
  }
}
